"use client";

import Image from "next/image";
import PriceDisplay from "@/components/card/card-component/PriceDisplay";
import RatingReviewBadge from "@/components/card/card-component/RatingReviewBadge";
import FavoriteIconButton from "@/components/card/card-component/FavoriteIconButton";
import CardStatusBadges from "@/components/card/card-component/CardStatusBadges";
import { Link, useRouter } from "@/i18n/navigation";
import { IconCircleCheck, IconMapPin } from "@tabler/icons-react";
import { useHotelHref } from "@/hooks/url-state/useHotelQuery";
import { cn, FALLBACK_FEATURE_IMAGE } from "@/lib/utils";
import { Typography } from "@/components/Typography";
import { memo } from "react";
import { OverallReview } from "../main-services/shared/review/review.type";

export type HotelCardTypeItems = {
  id: number;
  feature: string | null;
  title: string;
  price: number | null;
  salePrice: number | null;
  isFavorite: boolean;
  isInstantConfirm: boolean;
  overallReview: OverallReview | null;
  starRating: number | null;
  hasBreakfast: number | null;
  cancellationNote: string | null;
  located: string | null;
  disPercent: number | null;
  availMsg: string;
  mbcTotalReviews: number | null;
  propertyType?: Record<string, string> | null;
};

export type HotelCardProps = {
  hotel: HotelCardTypeItems;
  className?: string;
  onFavoriteClick?: () => void;
  href?: string;
  shadow?: boolean;
};

/**
 * Card Update: 8 Oct 2026
 * 1. fallback to propertyType when starRating = 0 or null
 * 2. fallback to "Cancellation is subject to the property’s policy" when cancellationNote is null or undefined
 * 3. update hight to 340px (ui update)
 */
const HotelCard = ({
  hotel,
  className,
  href,
  shadow = true,
}: HotelCardProps) => {
  const router = useRouter();
  const finalHref = useHotelHref(hotel, href);

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest("a")) {
      return;
    }
    router.push(finalHref as never);
  };

  const isHotelOrResort =
    hotel.propertyType?.name === "Hotel" ||
    hotel.propertyType?.name === "Boutique Hotel" ||
    hotel.propertyType?.name === "Resort";

  const hasHotelInfo = isHotelOrResort
    ? !!hotel.starRating
    : !!hotel.propertyType;
  return (
    <article
      onClick={handleCardClick}
      className={cn(
        "group relative h-[340px] w-full cursor-pointer rounded-2xl border p-0",
        shadow && "shadow-product-card",
        className,
      )}
    >
      <div className="flex h-full flex-col overflow-hidden rounded-2xl bg-white">
        <div className="relative h-[193px] w-full shrink-0 overflow-hidden rounded-t-2xl">
          <Image
            src={hotel.feature ?? FALLBACK_FEATURE_IMAGE}
            alt={"hotel-feature-image"}
            fill
            unoptimized
            className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
          />

          <div className="relative z-10">
            <FavoriteIconButton
              itemId={hotel.id}
              svcType="hotel"
              ariaLabel="Save hotel"
              active={hotel.isFavorite}
              className="absolute left-2 top-2"
            />
          </div>

          <CardStatusBadges
            isInstantConfirm={hotel?.isInstantConfirm}
            className="absolute top-2 right-2"
          />

          <div className="absolute bottom-2 left-2 right-2 flex items-end justify-between gap-2">
            <RatingReviewBadge
              rating={hotel.overallReview?.overallRating}
              reviewCount={hotel.mbcTotalReviews}
              className="shrink-0"
            />

            <CardStatusBadges
              disPercent={hotel?.disPercent}
              className={cn(
                "absolute right-0",
                hotel?.availMsg ? "bottom-7" : "bottom-0",
              )}
            />
            <CardStatusBadges
              availMsg={Number(hotel?.availMsg)}
              className="absolute bottom-0 right-0"
            />
          </div>
        </div>

        <section className="flex w-full flex-1 flex-col gap-2 bg-white p-3">
          <Typography
            variant="subtitle1"
            className="line-clamp-1 group-hover:text-secondary"
          >
            {hotel.title}
          </Typography>

          <div className="flex min-w-0 gap-1 items-center">
            <IconCircleCheck
              className="size-4.5 shrink-0 text-success"
              stroke={1.8}
            />
            <Typography variant="body2" className="truncate text-success">
              {hotel.cancellationNote || "Property cancellation policy apply"}
            </Typography>
          </div>

          <section className="flex items-center gap-1">
            {/*             
            {hotel.starRating ? (
              <Typography variant="body2" className="shrink-0">
                {hotel.starRating} {hotel.starRating === 1 ? "Star" : "Stars"}
              </Typography>
            ) : hotel.propertyType ? (
              <Typography variant="body2" className="shrink-0 capitalize">
                {hotel.propertyType.name}
              </Typography>
            ) : null} */}
            {isHotelOrResort ? (
              hotel.starRating ? (
                <Typography variant="body2" className="shrink-0">
                  {hotel.starRating} {hotel.starRating === 1 ? "Star" : "Stars"}
                </Typography>
              ) : null
            ) : hotel.propertyType ? (
              <Typography variant="body2" className="shrink-0 capitalize">
                {hotel.propertyType.name}
              </Typography>
            ) : null}

            {hotel.hasBreakfast ? (
              <>
                {/* {hotel.starRating || hotel.propertyType ? (
                  <span
                    aria-hidden="true"
                    className="h-1 w-1 rounded-full bg-caption shrink-0"
                  />
                ) : null} */}
                {hasHotelInfo ? (
                  <span
                    aria-hidden="true"
                    className="h-1 w-1 rounded-full bg-caption shrink-0"
                  />
                ) : null}

                <Typography
                  variant="body2"
                  className="min-w-0 truncate text-success"
                >
                  Breakfast included
                </Typography>
              </>
            ) : null}
          </section>

          {/* Price */}
          <section className="w-full flex items-end justify-between gap-2 absolute bottom-3 left-0 px-3">
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <IconMapPin
                className="h-4 w-4 shrink-0 text-body"
                strokeWidth={1.8}
              />
              <Typography variant="body2" className="truncate text-body">
                {hotel.located ?? "-"}
              </Typography>
            </div>

            <PriceDisplay
              price={hotel.price}
              salePrice={hotel.salePrice || undefined}
              className="shrink-0"
            />
          </section>
        </section>
      </div>

      <Link
        href={finalHref as never}
        aria-label={`View ${hotel.title} details`}
        className="absolute inset-0 z-10 rounded-2xl"
      />
    </article>
  );
};

export default memo(HotelCard);
